import { CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collaboratorTypeHasRole,
  loadSessionStorage,
  RIF,
  isInBetaType,
  sortBy,
  useCollaboratorTypeInProject,
  useCurrentProjectState,
  useCurrentWorkspaceAccountPlan,
  useCurrentWorkspaceState,
  useSearch,
} from "../../lib";
import {
  useCreateDispatchActions,
  useSelectBatchData,
  useSelectProfile,
  useSelectTheme,
  useSelectSettings,
} from "../../store";

import {
  Button,
  ButtonReverse,
  CalcSlotsPop,
  Input,
  ParticipantNotificationDiscardPop,
  ParticipantNotificationInfoPop,
  ParticipantPopup,
  PricingPage,
  ProjectSwitchBar,
  TooltipHover,
  ParticipantTagsFilter,
  ParticipantStatusFilter,
  PARTICIPANT_STATUS_OPTIONS,
  type ParticipantStatusFilterOption,
  ParticipantTable,
  PopupSendNotification,
} from "..";

import {
  DoubleRightwardArrowIcon,
  ParticipantTutorialStepOneIcon,
  ParticipantTutorialStepThreeIcon,
  ParticipantTutorialStepTwoIcon,
  ProfileWhiteIcon,
  SearchIcon,
} from "../../asset/image";

import ParticipantsBackground from "../../asset/image/participants_background.png";
import { AttemptRequestParticipantStatus } from "./request_project_data_hook";
import {
  BetaType,
  IParticipant,
  ITag,
  ProjectCollaboratorRole,
  WorkspaceCollaboratorRole,
} from "../../shared/db";
import _ from "lodash";
import { ProductPlan } from "../../shared/db/product";

export const ParticipantsPage = () => {
  const { color, pad, fontWeight, fontSize } = useSelectTheme();
  const settings = useSelectSettings();
  const canUseBulkCompleteParticipants = isInBetaType({ betaType: BetaType.Internal, settings });
  const { doREQUEST_PARTICIPANT_COMPLETE_STATUS_UPDATE, doBATCH_UPDATE_PARTICIPANT } =
    useCreateDispatchActions();

  /* ------------------ reducer & store basic state ------------------ */
  const { projectId, project } = useCurrentProjectState();
  const { workspace: currentWorkspace } = useCurrentWorkspaceState();
  const collaboratorType = useCollaboratorTypeInProject();
  const canCreateParticipant = collaboratorTypeHasRole({
    collaboratorType,
    projectRole: ProjectCollaboratorRole.ParticipantCreate,
    workspaceRole: WorkspaceCollaboratorRole.ParticipantCreate,
  });
  const canUpdateParticipant = collaboratorTypeHasRole({
    collaboratorType,
    projectRole: ProjectCollaboratorRole.ParticipantCreate,
    workspaceRole: WorkspaceCollaboratorRole.ParticipantCreate,
  });

  const batchId = project?.batchList?.[0]?.id;
  const loadSession = loadSessionStorage();

  /* ------------------ participants state ------------------ */
  const participantList: IParticipant[] =
    useSelectBatchData()?.[batchId ?? ""]?.participantList || [];
  const { searchValue, searchResult, setSearchValue } = useSearch("insignia", participantList);
  const [displayParticipantPopup, setDisplayParticipantPopup] = useState(false);
  const [tooltipHoverState, setTooltipHoverState] = useState({ upgradePlan: false });
  const [renderAddParticipantButtonTooltip, setRenderAddParticipantButtonTooltip] = useState(false);
  const [selectedList, setSelectedList] = useState<IParticipant[]>([]);
  // notification state
  const [notificationList, setNotificationList] = useState<IParticipant[]>([]);
  const [notificationInfo, setNotificationInfo] = useState<{
    message: { msgTitle: string; msgContent: string };
    response: any;
  }>({
    message: {
      msgTitle: "",
      msgContent: "",
    },
    response: {},
  });
  const [displayPopupSendNotification, setDisplayPopupSendNotification] = useState(false);
  const [displayNotificationDiscardPop, setDisplayNotificationDiscardPop] = useState(false);
  const [displayNotificationInfoPop, setDisplayNotificationInfoPop] = useState(false);

  // tag filter state
  const [untagged, setUntagged] = useState<boolean>(true);
  const [selectedTagList, setSelectedTagList] = useState<ITag[]>([]);

  // participant status filter state
  const [selectedStatusList, setSelectedStatusList] = useState<ParticipantStatusFilterOption[]>([
    ...PARTICIPANT_STATUS_OPTIONS,
  ]);

  const fullTagList = useMemo(() => {
    if (!participantList.length) return [];
    return _.uniqBy(
      participantList.flatMap((p) => p.tagList),
      "id",
    ).filter((tag) => tag?.id) as ITag[];
  }, [participantList]);

  const displayParticipantList = useMemo(() => {
    const tagFilteredList =
      !fullTagList.length || (selectedTagList.length === fullTagList.length && untagged)
        ? (searchResult ?? [])
        : (searchResult ?? []).filter((participant) => {
            if (untagged) {
              if (!participant.tagList || participant.tagList.length === 0) {
                return true;
              }
            }
            return selectedTagList.some(
              (tag) =>
                participant.tagList &&
                participant.tagList.length > 0 &&
                participant.tagList.some((t: ITag) => t.id === tag.id),
            );
          });

    if (selectedStatusList.length === PARTICIPANT_STATUS_OPTIONS.length) return tagFilteredList;
    if (selectedStatusList.length === 0) return [];

    return tagFilteredList.filter((participant) =>
      selectedStatusList.some((status) => {
        const hasCompletedDate = participant.completedDate != null;

        if (status === "notJoined") return !participant.activated;
        if (!participant.activated) return false;
        if (status === "completed") return hasCompletedDate;
        return !hasCompletedDate;
      }),
    );
  }, [searchResult, fullTagList.length, selectedTagList, untagged, selectedStatusList]);

  useMemo(() => {
    if (_.isEqual(fullTagList, selectedTagList)) return;
    setSelectedTagList(fullTagList);
  }, [fullTagList]);

  // account state
  const { accountPlan, hasActivePlanPurchase } = useCurrentWorkspaceAccountPlan({ projectId });
  const participantLimit = hasActivePlanPurchase ? currentWorkspace?.participantLimit : 5;

  AttemptRequestParticipantStatus({ forceBatchFetch: true });

  const [displayCalcSlotsPop, setDisplayCalcSlotsPop] = useState(false);
  const slotsLimit = useMemo(() => {
    if (loadSession?.totalParticipants >= (participantLimit ?? 0)) return true;
    return false;
  }, [loadSession?.totalParticipants, participantLimit]);

  const [displayChangePlanPage, setDisplayChangePlanPage] = useState(false);
  const [bulkCompleteState, setBulkCompleteState] = useState<{
    inProgress: boolean;
    total: number;
    done: number;
    success: number;
    fail: number;
  }>({ inProgress: false, total: 0, done: 0, success: 0, fail: 0 });
  const bulkCompleteParticipantByIdRef = useRef<Record<string, IParticipant>>({});
  const bulkCompleteSuccessIdListRef = useRef<Set<string>>(new Set());
  const [bulkCompleteSuccessIdList, setBulkCompleteSuccessIdList] = useState<string[]>([]);

  const bulkCompleteCandidateList = useMemo(() => {
    return (selectedList ?? []).filter((p) => {
      if (!p?.id) return false;
      if (!p.activated) return false;
      const isParticipantMarkCompleted = p.completedDate != null || p.markCompletedAt != null;
      return !isParticipantMarkCompleted;
    });
  }, [selectedList]);

  useEffect(() => {
    if (!bulkCompleteSuccessIdList.length) return;
    const currentDate = new Date();
    bulkCompleteSuccessIdList.forEach((participantId) => {
      if (bulkCompleteSuccessIdListRef.current.has(participantId)) return;
      bulkCompleteSuccessIdListRef.current.add(participantId);
      const participant = bulkCompleteParticipantByIdRef.current[participantId];
      if (!participant?.batchId) return;
      doBATCH_UPDATE_PARTICIPANT({
        batchId: participant.batchId,
        participant: {
          ...participant,
          completedDate: currentDate,
          markCompletedAt: currentDate,
        },
      });
    });
  }, [bulkCompleteSuccessIdList]);

  const handleBulkCompleteParticipants = () => {
    if (!canUseBulkCompleteParticipants) return;
    if (!canUpdateParticipant) return;
    if (!bulkCompleteCandidateList.length) return;
    bulkCompleteParticipantByIdRef.current = Object.fromEntries(
      bulkCompleteCandidateList.map((p) => [p.id, p]),
    );
    bulkCompleteSuccessIdListRef.current = new Set();
    setBulkCompleteSuccessIdList([]);

    setBulkCompleteState({
      inProgress: true,
      total: bulkCompleteCandidateList.length,
      done: 0,
      success: 0,
      fail: 0,
    });

    bulkCompleteCandidateList.forEach((participant) => {
      doREQUEST_PARTICIPANT_COMPLETE_STATUS_UPDATE({
        setRequestResult: (resultAction) => {
          const result = resultAction as unknown as { success?: boolean } | null;
          setBulkCompleteState((prev) => {
            const next = {
              ...prev,
              done: prev.done + 1,
              success: prev.success + (result?.success ? 1 : 0),
              fail: prev.fail + (!result?.success ? 1 : 0),
            };
            if (next.done >= next.total) {
              next.inProgress = false;
            }
            return next;
          });

          if (result?.success) {
            setBulkCompleteSuccessIdList((prev) =>
              prev.includes(participant.id) ? prev : [...prev, participant.id],
            );
          }
        },
        payload: {
          participantId: participant.id,
          complete: true,
          force: true,
        },
      });
    });
  };

  /* ------------------ other effect ------------------ */
  useEffect(() => {
    let tempState = [...selectedList];
    displayParticipantList?.map((item) => {
      if (!item.activated) {
        tempState = tempState.filter((el) => el.id !== item.id);
      }
    });
    setNotificationList(tempState);
  }, [selectedList]);

  return (
    <div>
      {RIF(
        displayChangePlanPage,
        <PricingPage
          {...{
            setDisplayChangePlanPage,
          }}
        />,
      )}

      <ProjectSwitchBar projectPanel="Participants" />

      {RIF(
        displayParticipantPopup && projectId,
        <ParticipantPopup
          {...{
            method: "Add",
            closeAction: () => {
              setDisplayParticipantPopup(false);
            },
            projectId,
            batchId,
            setSearchValue,
          }}
        />,
      )}

      {RIF(displayCalcSlotsPop, <CalcSlotsPop {...{ setDisplayCalcSlotsPop }} />)}

      {RIF(
        displayPopupSendNotification,
        <PopupSendNotification
          {...{
            sendNotificationParticipantIdList: notificationList.map((item) => item.id),
            setNotificationInfo,
            setDisplayPopupSendNotification,
            setDisplayNotificationDiscardPop,
            setDisplayNotificationInfoPop,
          }}
        />,
      )}

      {RIF(
        displayNotificationDiscardPop,
        <ParticipantNotificationDiscardPop
          {...{
            setDisplayNotificationDiscardPop,
            setDisplayPopupSendNotification,
          }}
        />,
      )}

      {RIF(
        displayNotificationInfoPop,
        <ParticipantNotificationInfoPop
          {...{
            notificationList,
            notificationInfo,
            closeAction: setDisplayNotificationInfoPop,
            setSelectedList,
          }}
        />,
      )}

      {/* page container */}
      <div
        style={{
          width: "100%",
          minHeight: "calc(100vh - 106px)",
          backgroundColor: color.background,
          padding: "30px 30px 60px",
        }}
        css={{
          "a:focus-visible, button:focus-visible, input:focus-visible": {
            outline: `2px solid ${color.primary}`,
            outlineOffset: "2px",
          },
        }}
      >
        <div
          style={{
            width: "100%",
            position: "relative",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "10px",
          }}
        >
          <h1 style={{ fontWeight: fontWeight.bold, fontSize: "20px", margin: 0 }}>Participants</h1>
          <div
            style={{
              height: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ marginRight: pad.large }}>
              <span data-testid="participant_amount">
                {currentWorkspace?.totalParticipants ?? 0}
              </span>
              /{participantLimit} participants added
            </div>
            {RIF(
              !slotsLimit,
              <Button
                onClick={() => {
                  setDisplayParticipantPopup(true);
                }}
                css={{ position: "relative" }}
                btnPadding="medium"
                disabled={!canCreateParticipant}
                onMouseOver={() => setRenderAddParticipantButtonTooltip(true)}
                onMouseLeave={() => setRenderAddParticipantButtonTooltip(false)}
              >
                <>
                  <img
                    aria-hidden={true}
                    alt="Add participant icon"
                    src={ProfileWhiteIcon}
                    width="12"
                    style={{ marginRight: "7px" }}
                  />
                  Add Participant
                  {RIF(
                    renderAddParticipantButtonTooltip && !canCreateParticipant,
                    <TooltipHover
                      {...{
                        tooltipWidth: "150px",
                        title:
                          "Viewers cannot add participants. If you want to add participants, please ask an admin to upgrade your role for this project.",
                      }}
                    />,
                  )}
                </>
              </Button>,
            )}
            {RIF(
              slotsLimit,
              <div
                onMouseOver={() => setTooltipHoverState((prev) => ({ ...prev, upgradePlan: true }))}
                onMouseOut={() => setTooltipHoverState((prev) => ({ ...prev, upgradePlan: false }))}
              >
                <Button
                  css={{ position: "relative" }}
                  onClick={() => {
                    if (accountPlan !== ProductPlan.Advanced || !hasActivePlanPurchase) {
                      setDisplayChangePlanPage(true);
                    } else {
                      setDisplayCalcSlotsPop(true);
                    }
                  }}
                  btnColor="upgrade"
                  btnPadding="medium"
                >
                  <>
                    <img
                      alt="Add participant icon"
                      src={ProfileWhiteIcon}
                      width="12"
                      style={{ marginRight: "7px" }}
                    />
                    Add Participant
                    {RIF(
                      tooltipHoverState.upgradePlan,
                      <TooltipHover
                        {...{
                          tooltipWidth: "150px",
                          title: !hasActivePlanPurchase
                            ? `Free plans have a 5 participant limit. Please upgrade to add more participants.`
                            : accountPlan === ProductPlan.Advanced
                              ? `Advanced plans have a 20 participant limit throughout all projects.   
                        To add additional participants please buy more participant slots.`
                              : accountPlan === ProductPlan.Basic
                                ? `Basic plans have a 20 participant limit. Please upgrade to add more participants.`
                                : `Free plans have a 5 participant limit. Please upgrade to add more participants.`,
                        }}
                      />,
                    )}
                  </>
                </Button>
              </div>,
            )}
          </div>
        </div>

        {/* participant tutorial */}
        {RIF(
          !!participantList?.length,
          <>
            <div
              style={{
                width: "100%",
                padding: "24px 24px 32px 32px",
                borderRadius: "5px",
                backgroundColor: "rgba(154, 171, 255, 0.16)",
                position: "relative",
                marginBottom: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div
                style={{
                  width: "6px",
                  height: "100%",
                  position: "absolute",
                  top: 0,
                  left: 0,
                  backgroundColor: color.primary,
                }}
              />
              <div>
                <h2
                  style={{
                    fontWeight: fontWeight.thick,
                    fontSize: fontSize.h5,
                    marginBottom: "8px",
                  }}
                >
                  Participant Setup Guide
                </h2>
                <p
                  style={{
                    fontWeight: fontWeight.medium,
                    width: "264px",
                  }}
                >
                  Ask your participants to follow the steps below to join your project:
                </p>
              </div>
              <div
                style={{
                  display: "flex",
                }}
              >
                <img
                  style={{ marginRight: "16px" }}
                  width="70"
                  src={ParticipantTutorialStepOneIcon}
                  aria-hidden={true}
                  alt="Labfront icon showing download"
                />
                <div>
                  <h3
                    style={{
                      fontWeight: fontWeight.thick,
                      color: color.grey_600,
                      marginBottom: "8px",
                    }}
                  >
                    Step 1
                  </h3>
                  <p
                    style={{
                      width: "11.429rem",
                      fontWeight: fontWeight.medium,
                    }}
                  >
                    Download the Labfront Companion app{" "}
                    <a
                      href="https://www.labfront.com/download-app"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: color.primary,
                        fontWeight: fontWeight.thick,
                        textDecoration: "none",
                        cursor: "pointer",
                      }}
                    >
                      here
                    </a>
                  </p>
                </div>
              </div>
              <img width="24" src={DoubleRightwardArrowIcon} aria-hidden={true} alt="" />
              <div
                style={{
                  display: "flex",
                }}
              >
                <img
                  style={{ marginRight: "16px" }}
                  width="70"
                  src={ParticipantTutorialStepTwoIcon}
                  aria-hidden={true}
                  alt="Code being input in app"
                />
                <div>
                  <h3
                    style={{
                      fontWeight: fontWeight.thick,
                      color: color.grey_600,
                      marginBottom: "8px",
                    }}
                  >
                    Step 2
                  </h3>
                  <p
                    style={{
                      width: "11.429rem",
                      fontWeight: fontWeight.medium,
                    }}
                  >
                    Enter the 6 character invite code into Labfront app
                  </p>
                </div>
              </div>
              <img width="24" src={DoubleRightwardArrowIcon} aria-hidden={true} alt="" />
              <div
                style={{
                  display: "flex",
                }}
              >
                <img
                  style={{ marginRight: "16px" }}
                  width="70"
                  src={ParticipantTutorialStepThreeIcon}
                  aria-hidden={true}
                  alt="Participant holding phone with Labfront app open"
                />
                <div>
                  <h3
                    style={{
                      fontWeight: fontWeight.thick,
                      color: color.grey_600,
                      marginBottom: "8px",
                    }}
                  >
                    Step 3
                  </h3>
                  <p
                    style={{
                      width: "11.429rem",
                      fontWeight: fontWeight.medium,
                    }}
                  >
                    Follow the instructions to complete setup
                  </p>
                </div>
              </div>
            </div>
            <p
              style={{
                color: color.grey_600,
                fontWeight: fontWeight.medium,
                marginBottom: "53px",
              }}
            >
              Note: We recommend you try this yourself before inviting participants.
            </p>
          </>,
        )}

        {RIF(
          participantList?.length === 0,
          <div
            style={{
              width: "100%",
              height: "calc(100vh - 200px)",
              display: "flex",
              border: `1px dotted ${color.border.disabled}`,
              paddingTop: "170px",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <img src={ParticipantsBackground} width="25%" alt="" />
            <div
              style={{
                fontSize: "18px",
                fontWeight: fontWeight.medium,
                marginTop: "25px",
              }}
            >
              No participants yet
            </div>
            <div
              style={{
                fontSize: "16px",
                fontWeight: fontWeight.medium,
                color: color.grey_400,
                marginTop: "12px",
              }}
            >
              Add a participant to start collecting data.
            </div>
          </div>,
        )}

        {RIF(
          participantList?.length > 0,
          <>
            {/* search field */}
            <div
              style={{
                marginBottom: pad.small,
                display: "flex",
                alignItems: "center",
                gap: "16px",
              }}
            >
              <p>{participantList?.length} Participant(s)</p>
              <div
                style={
                  {
                    width: "200px",
                    height: "32px",
                    border: `1px solid ${color.grey_160}`,
                    borderRadius: "5px",
                    display: "flex",
                    alignItems: "center",
                    background: color.white,
                    position: "relative",
                    justifyContent: "space-between",
                    ":hover": {
                      border: `1px solid ${color.grey_400}`,
                      cursor: "pointer",
                    },
                  } as CSSProperties
                }
              >
                <label
                  htmlFor="participants-search"
                  style={{
                    border: 0,
                    clip: "rect(0 0 0 0)",
                    height: "1px",
                    margin: "-1px",
                    overflow: "hidden",
                    padding: 0,
                    position: "absolute",
                    whiteSpace: "nowrap",
                    width: "1px",
                  }}
                >
                  Search participants
                </label>
                <Input
                  id="participants-search"
                  value={searchValue}
                  onChange={(e: any) => setSearchValue(e.target.value)}
                  placeholder="Search Participants"
                  style={{
                    border: "none",
                    background: "inherit",
                    height: "100%",
                    width: "100%",
                    paddingRight: "32px",
                  }}
                />
                <img
                  src={SearchIcon}
                  width="16"
                  style={{ position: "absolute", right: "8px", top: "8px" }}
                  alt={"Magnifying glass"}
                />
              </div>
              {RIF(
                fullTagList.length > 0,
                <ParticipantTagsFilter
                  {...{
                    fullTagList,
                    selectedTagList,
                    setSelectedTagList,
                    untagged,
                    setUntagged,
                  }}
                />,
              )}
              <ParticipantStatusFilter
                {...{
                  selectedStatusList,
                  setSelectedStatusList,
                }}
              />

              {RIF(
                Object.keys(selectedList).length > 0,
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "8px",
                  }}
                >
                  <div>{Object.keys(selectedList).length} Selected</div>

                  <ButtonReverse
                    onClick={() => setDisplayPopupSendNotification(true)}
                    disabled={Object.keys(notificationList)?.length === 0 || !canUpdateParticipant}
                    btnPadding="mediumSmall"
                  >
                    <div
                      css={{
                        color:
                          Object.keys(notificationList)?.length === 0
                            ? color.textIcon.disabled
                            : color.primary,
                      }}
                    >
                      Send message to active participants ({Object.keys(notificationList).length})
                    </div>
                  </ButtonReverse>

                  {RIF(
                    canUseBulkCompleteParticipants,
                    <ButtonReverse
                      onClick={handleBulkCompleteParticipants}
                      disabled={
                        !canUpdateParticipant ||
                        bulkCompleteCandidateList.length === 0 ||
                        bulkCompleteState.inProgress
                      }
                      btnPadding="mediumSmall"
                    >
                      <div
                        css={{
                          color:
                            !canUpdateParticipant ||
                            bulkCompleteCandidateList.length === 0 ||
                            bulkCompleteState.inProgress
                              ? color.textIcon.disabled
                              : color.primary,
                        }}
                      >
                        {bulkCompleteState.inProgress
                          ? `Completing... (${bulkCompleteState.done}/${bulkCompleteState.total})`
                          : `Mark completed (${bulkCompleteCandidateList.length})`}
                      </div>
                    </ButtonReverse>,
                  )}
                </div>,
              )}
            </div>

            <ParticipantTable
              {...{
                selectedList,
                setSelectedList,
                displayParticipantList,
                setDisplayPopupSendNotification,
                projectTagList: project?.tagList ?? [],
              }}
            />
          </>,
        )}
      </div>
    </div>
  );
};
